import EmptyState from './EmptyState';

// The empty-state copy for the two payment views (לתשלום = /suppliers,
// היסטורית תשלומים = /suppliers/paid). One component so both tabs speak with the
// same voice and the wording lives in exactly one place.
//
// The page computes `kind` from its own data (all client-side, no extra fetch —
// each tab's payload already contains the other tab's rows, just filtered out)
// and passes the callbacks. Copy notes:
//   • Gender-neutral throughout (infinitive, impersonal) — the audience is mixed.
//   • Tab NAVIGATION always names the real tab (לתשלום / היסטורית תשלומים), never
//     a paraphrase like "חוב פתוח".
//   • Never says "סינון" (filter) — that concept is gone from this screen.
//
// kinds:
//   all-paid      (unpaid tab) supplier searched, all their docs already paid
//   nothing-paid  (paid tab)   supplier searched, nothing paid yet
//   clean-period  (unpaid)     no search, docs exist in range but all paid
//   empty-range   both         no search, nothing in the range
//   new-account   both         nothing at all (range cleared, still empty)
//   no-match-range both        search finds nothing AND a date range is set
//   unknown       both         search finds nothing with NO range (all dates already)
//   fallback      both         some other overflow filter is active — stay generic

// The supplier name is always shown as לספק "{name}". The prefix lamed (ל)
// attaches to a word in Hebrew, so "ל {name}" reads with an awkward gap and
// "ל{name}" jams the name onto the ל; naming the noun (ספק) and quoting the name
// reads cleanly for any name shape.
function forSupplier(name) {
  return `לספק "${name}"`;
}

function quote(name) {
  return `"${name}"`;
}

export default function PaymentEmptyState({ tab, kind, supplierName = '', period = '', onJump, onSearchAllDates }) {
  const paid = tab === 'paid';
  const name = supplierName.trim();
  // Name the actual period instead of saying "this range". An empty table that
  // doesn't say WHICH period is empty reads as a broken app — especially on
  // mobile, where the range used to be invisible unless you opened סינון.
  // The caller passes '' when no range is set, so we fall back to the generic
  // wording rather than producing "בכל הזמן".
  const inRange = period ? `ב${period}` : 'בטווח הזה';

  // [headline, sub, action]. action is { label, onClick } or null.
  let headline = '';
  let sub = '';
  let action = null;

  switch (kind) {
    case 'all-paid':
      headline = 'הכול שולם לספק הזה';
      sub = `אין כרגע סכום לתשלום ${forSupplier(name)} בטווח שנבחר.`;
      action = { label: `הצג את ${name} ב'היסטורית תשלומים'`, onClick: onJump };
      break;
    case 'nothing-paid':
      headline = 'עוד לא שולם לספק הזה';
      sub = `${forSupplier(name)} יש סכום לתשלום בטווח שנבחר.`;
      action = { label: `הצג את ${name} ב'לתשלום'`, onClick: onJump };
      break;
    case 'clean-period':
      headline = `אין סכומים לתשלום ${inRange}`;
      sub = 'הכול משולם — אין ספקים שממתינים לתשלום.';
      break;
    case 'empty-range':
      headline = paid ? `לא בוצעו תשלומים ${inRange}` : `אין סכומים לתשלום ${inRange}`;
      break;
    case 'new-account':
      headline = paid ? 'עוד אין היסטורית תשלומים' : 'עוד אין ספקים במערכת';
      sub = paid ? 'תשלומים שיבוצעו יופיעו כאן.' : 'כשיתווספו מסמכים, הסכומים לתשלום יופיעו כאן.';
      break;
    case 'no-match-range':
      headline = paid ? `אין תשלומים ${inRange}` : `אין תוצאות ${inRange}`;
      sub = paid
        ? `לא נמצאו תשלומים ${forSupplier(name)} בטווח שנבחר.`
        : `לא נמצא ספק בשם ${quote(name)} עם סכום לתשלום בטווח שנבחר.`;
      action = { label: 'חפש בכל התאריכים', onClick: onSearchAllDates };
      break;
    case 'unknown':
      headline = 'לא נמצא ספק בשם הזה';
      sub = 'אפשר לבדוק את האיות או לנסות שם חלקי.';
      break;
    case 'fallback':
    default:
      headline = 'אין תוצאות לחיפוש הזה בטווח התאריכים שנבחר';
      break;
  }

  // The welcoming "nothing yet" moment gets the mermaid illustration; every other
  // state is a transient "adjust your search" moment and stays quiet (compact).
  const compact = kind !== 'new-account';

  return (
    <EmptyState compact={compact} message={headline}>
      {sub && <p className="empty-subline">{sub}</p>}
      {action && (
        <button type="button" className="empty-action" onClick={action.onClick}>
          {action.label}
        </button>
      )}
    </EmptyState>
  );
}
